<template>
    <TabGroup
        as="div"
        class="flex flex-col-reverse lg:flex-row lg:space-x-4 xl:space-x-8"
        :selectedIndex="selectedImage"
        @change="onChange"
    >
        <div class="mx-auto mt-6 w-full max-w-xs lg:mt-0 lg:w-20 xl:w-24">
            <TabList
                :class="[
                    hasVideo ? 'grid-cols-4' : 'grid-cols-3',
                    'grid lg:grid-cols-1 gap-4',
                ]"
            >
                <Tab
                    v-for="image in images"
                    :key="image.id"
                    class="relative flex h-16 lg:w-16 lg:h-16 xl:w-20 xl:h-20 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium text-gray-900 focus:outline-none"
                    v-slot="{ selected }"
                >
                    <span class="sr-only">{{ image.name }}</span>
                    <span class="absolute inset-0 overflow-hidden rounded-md">
                        <svg
                            v-if="image.video"
                            xmlns="http://www.w3.org/2000/svg"
                            class="mx-auto h-16 w-16 xl:w-20 xl:h-20 text-gray-700"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <img
                            v-else
                            :src="image.src"
                            :srcset="image.srcset"
                            :alt="image.name"
                            class="h-full w-full object-contain object-center"
                        />
                    </span>
                    <span
                        :class="[
                            selected
                                ? 'ring-nutreance-green ring-2 ring-offset-2'
                                : 'ring-gray-300 ring-1 ring-offset-1',
                            'pointer-events-none absolute inset-0 rounded-md',
                        ]"
                        aria-hidden="true"
                    />
                    <span
                        v-if="image.video"
                        class="absolute top-16 xl:top-20 mt-2 text-xs font-semibold"
                        >VIDEO</span
                    >
                </Tab>
            </TabList>
        </div>

        <TabPanels class="aspect-h-1 aspect-w-1 w-full">
            <TabPanel v-for="image in images" :key="image.id">
                <img
                    v-if="!image.video"
                    :src="image.src"
                    :srcset="image.srcset"
                    :alt="image.name"
                    class="h-full w-full object-contain object-center rounded-lg"
                />
            </TabPanel>
        </TabPanels>
    </TabGroup>
</template>

<script setup lang="ts">
import { inject, ref } from "vue";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
import { useStaticAsset } from "../../functions/useStaticAsset";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    hasVideo?: string;
    product?: any;
}>();

const { asset } = useStaticAsset();
const emitter = inject<AppEmitter>("mitt")!;
const selectedImage = ref(0);

const images: any[] = [
    {
        id: 0,
        name: `${props.product.name} Bottle`,
        src: imgSrc("bottle-spilled-front@1x.jpg", "1x"),
        srcset: imgSrcset("bottle-spilled-front@1x.jpg"),
    },
    {
        id: 1,
        name: `${props.product.name} Supplement Facts`,
        src: imgSrc("bottle-supplement-facts@1x.jpg", "1x"),
        srcset: imgSrcset("bottle-supplement-facts@1x.jpg"),
    },
    {
        id: 2,
        name: `${props.product.name} Directions`,
        src: imgSrc("bottle-directions@1x.jpg", "1x"),
        srcset: imgSrcset("bottle-directions@1x.jpg"),
    },
];

if (props.hasVideo) {
    images.push({
        id: 3,
        name: `${props.product.name} Video`,
        video: true,
    });
}

function imgSrc(src: string, size: string) {
    const slug = props.product.slug;
    const upc = props.product.default_product_option.upc;
    const image = src.replace("@1x", `@${size}`);

    return asset(`/images/products/${slug}/${upc}/${image}`);
}

function imgSrcset(src: string) {
    return `${imgSrc(src, "1x")} 1x, ${imgSrc(src, "2x")} 2x, ${imgSrc(src, "3x")} 3x`;
}

function onChange(id: number) {
    if (images[id].video) {
        emitter.emit("openProductVideoModal");
    }

    selectedImage.value = id;
}

emitter.on("closeProductVideoModal", () => (selectedImage.value = 0));
</script>
